import React, { useState, useEffect } from 'react';
import { AgentAvatar } from './AgentAvatars';

const AGENTS_AT_WORK = [
  { id: 'kathy', name: 'Kathy', task: 'Generating onboarding checklist...', output: 'New hire welcome packet ready', icon: '📋' },
  { id: 'sophie', name: 'Sophie', task: 'Screening 47 resumes...', output: 'Top 5 candidates shortlisted', icon: '🔍' },
  { id: 'john', name: 'John', task: 'Running compliance audit...', output: 'GDPR check passed', icon: '🛡' },
  { id: 'omar', name: 'Omar', task: 'Processing monthly payroll...', output: 'Payment summaries generated', icon: '💰' },
  { id: 'priya', name: 'Priya', task: 'Updating benefits enrollment...', output: 'Open enrollment guide sent', icon: '🎁' },
  { id: 'linda', name: 'Linda', task: 'Designing training module...', output: '90-day learning path created', icon: '📚' },
  { id: 'alex', name: 'Alex', task: 'Analyzing performance data...', output: 'Quarterly review reports ready', icon: '📊' },
  { id: 'felix', name: 'Felix', task: 'Scheduling 8 interviews...', output: 'Calendar invites sent', icon: '📅' },
  { id: 'jasmine', name: 'Jasmine', task: 'Tracking diversity metrics...', output: 'D&I report published', icon: '🌍' },
  { id: 'megan', name: 'Megan', task: 'Creating training manual...', output: 'PDF document exported', icon: '✏️' },
  { id: 'mario', name: 'Mario', task: 'Reviewing employee concern...', output: 'Resolution plan drafted', icon: '🤝' },
];

export default function AgentWorkplaceAnimation() {
  const [activeAgent, setActiveAgent] = useState(0);
  const [phase, setPhase] = useState('typing'); // typing, processing, complete
  const [chatBubbles, setChatBubbles] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);

  useEffect(() => {
    const cycle = () => {
      setPhase('typing');
      setTimeout(() => setPhase('processing'), 1800);
      setTimeout(() => {
        setPhase('complete');
        setCompletedTasks(prev => [...prev.slice(-4), AGENTS_AT_WORK[activeAgent].id]);
      }, 3500);
      setTimeout(() => {
        setActiveAgent(prev => (prev + 1) % AGENTS_AT_WORK.length);
        setPhase('typing');
      }, 5000);
    };
    cycle();
    const interval = setInterval(cycle, 5000);
    return () => clearInterval(interval);
  }, [activeAgent]);

  const current = AGENTS_AT_WORK[activeAgent];
  const visibleAgents = [
    AGENTS_AT_WORK[(activeAgent + 0) % AGENTS_AT_WORK.length],
    AGENTS_AT_WORK[(activeAgent + 1) % AGENTS_AT_WORK.length],
    AGENTS_AT_WORK[(activeAgent + 2) % AGENTS_AT_WORK.length],
    AGENTS_AT_WORK[(activeAgent + 3) % AGENTS_AT_WORK.length],
    AGENTS_AT_WORK[(activeAgent + 4) % AGENTS_AT_WORK.length],
    AGENTS_AT_WORK[(activeAgent + 5) % AGENTS_AT_WORK.length],
  ];

  return (
    <div style={{ position: 'relative', width: '100%', maxWidth: 560 }}>
      {/* Office Background */}
      <div style={{
        background: 'linear-gradient(145deg, #f8fafc 0%, #e2e8f0 50%, #f1f5f9 100%)',
        borderRadius: 24, padding: '28px 24px', position: 'relative', overflow: 'hidden',
        border: '1px solid #e2e8f0', boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
      }}>
        {/* Decorative office elements */}
        <div style={{ position: 'absolute', top: 12, right: 16, fontSize: 10, color: '#94a3b8', fontWeight: 600, letterSpacing: 1 }}>PANZIX HQ — LIVE</div>
        <div style={{ position: 'absolute', top: 12, right: 16, width: 8, height: 8, borderRadius: '50%', background: '#22c55e', animation: 'pulse-dot 2s infinite', marginRight: -16, marginTop: -2 }} />

        {/* Agent Grid - Office Desks */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
          {visibleAgents.map((agent, i) => {
            const isActive = agent.id === current.id;
            const isCompleted = completedTasks.includes(agent.id);
            return (
              <div key={agent.id + i} style={{
                background: isActive ? 'white' : 'rgba(255,255,255,0.7)',
                borderRadius: 14, padding: '14px 10px', textAlign: 'center',
                border: isActive ? '2px solid #2E7D32' : '1px solid #e2e8f0',
                transform: isActive ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.4s ease',
                boxShadow: isActive ? '0 8px 24px rgba(46,125,50,0.15)' : 'none',
                position: 'relative',
              }}>
                {/* Status indicator */}
                <div style={{
                  position: 'absolute', top: 6, right: 6, width: 10, height: 10, borderRadius: '50%',
                  background: isActive ? '#22c55e' : isCompleted ? '#3b82f6' : '#e2e8f0',
                  transition: 'background 0.3s',
                }} />
                
                <div style={{ margin: '0 auto 6px', width: 52, height: 52, borderRadius: 12, overflow: 'hidden', border: isActive ? '2px solid #2E7D32' : '2px solid transparent', transition: 'border 0.3s' }}>
                  <AgentAvatar agentType={agent.id} size={48} />
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#0f172a' }}>{agent.name}</div>
                
                {/* Activity indicator */}
                {isActive && phase === 'typing' && (
                  <div style={{ display: 'flex', gap: 3, justifyContent: 'center', marginTop: 6 }}>
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#2E7D32', animation: 'bounce-dot 1.4s infinite' }} />
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#2E7D32', animation: 'bounce-dot 1.4s infinite 0.2s' }} />
                    <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#2E7D32', animation: 'bounce-dot 1.4s infinite 0.4s' }} />
                  </div>
                )}
                {isActive && phase === 'processing' && (
                  <div style={{ marginTop: 6, height: 4, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ height: '100%', background: 'linear-gradient(90deg, #2E7D32, #4CAF50)', borderRadius: 4, animation: 'progress-bar 1.5s ease-out forwards' }} />
                  </div>
                )}
                {isActive && phase === 'complete' && (
                  <div style={{ marginTop: 4, fontSize: 16, animation: 'pop-in 0.3s ease' }}>✅</div>
                )}
                {!isActive && isCompleted && (
                  <div style={{ marginTop: 4, fontSize: 9, color: '#64748b' }}>Done</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Active Task Display */}
        <div style={{
          background: 'white', borderRadius: 14, padding: '16px 18px',
          border: '1px solid #e2e8f0', position: 'relative',
          animation: 'slide-up-smooth 0.4s ease',
          key: current.id,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, overflow: 'hidden' }}>
              <AgentAvatar agentType={current.id} size={32} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{current.name}</div>
              <div style={{ fontSize: 10, color: '#64748b' }}>Working now...</div>
            </div>
            <span style={{ fontSize: 20 }}>{current.icon}</span>
          </div>
          
          {/* Task progress */}
          <div style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 14px', fontSize: 13 }}>
            {phase === 'typing' && (
              <div style={{ color: '#475569', animation: 'fade-in-text 0.5s ease' }}>
                <span style={{ color: '#94a3b8' }}>→</span> {current.task}
                <span style={{ animation: 'blink-cursor 1s infinite' }}>|</span>
              </div>
            )}
            {phase === 'processing' && (
              <div style={{ color: '#2E7D32', fontWeight: 600, animation: 'fade-in-text 0.3s ease' }}>
                ⚙️ Processing...
              </div>
            )}
            {phase === 'complete' && (
              <div style={{ color: '#166534', fontWeight: 600, animation: 'fade-in-text 0.3s ease' }}>
                ✅ {current.output}
              </div>
            )}
          </div>
        </div>

        {/* Connection lines between agents */}
        {phase === 'processing' && (
          <div style={{ position: 'absolute', top: '35%', left: '50%', width: 2, height: 30, background: 'linear-gradient(to bottom, #2E7D3200, #2E7D32, #2E7D3200)', animation: 'pulse-line 1s infinite' }} />
        )}
      </div>

      {/* Stats bar below */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 16 }}>
        {[
          { label: 'Tasks Today', value: completedTasks.length + 12, color: '#2E7D32' },
          { label: 'Active Agents', value: '6/11', color: '#7B1FA2' },
          { label: 'Avg Response', value: '< 30s', color: '#1565C0' },
        ].map(s => (
          <div key={s.label} style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
            <div style={{ fontSize: 10, color: '#94a3b8' }}>{s.label}</div>
          </div>
        ))}
      </div>

      <style>{`
        @keyframes bounce-dot {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-5px); }
        }
        @keyframes progress-bar {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        @keyframes pop-in {
          0% { transform: scale(0); }
          70% { transform: scale(1.3); }
          100% { transform: scale(1); }
        }
        @keyframes fade-in-text {
          0% { opacity: 0; transform: translateY(4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes blink-cursor {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes pulse-line {
          0%, 100% { opacity: 0; }
          50% { opacity: 1; }
        }
        @keyframes slide-up-smooth {
          0% { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
